import { DialogRef } from '@angular/cdk/dialog';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { waitForNavigationEnd } from '@domains/auth/components/login-modal/operators/wait-for-login-navigation.operator';
import { SubscribersStateService } from '@domains/subscriber/subscribers/subscribers-state.service';
import { ProbateService } from '@app/probate/shared/services/probate.service';
import { ToastService } from '@shared/components/toast';
import { tap, finalize, catchError, of } from 'rxjs';

import { getPhoneNumberToPlainNumber } from '@shared/utils/get-phone-number-to-plain-number.helper';
import { DashboardNavigationService } from '@shared/services/dashboard-navigation.service';
import { RegisterProbateFormService } from './register-probate-form.service';
import { ClerkCreateApplicationHttpService } from '../../shared/services/clerk-create-application-http.service';
import { CourtService, LoaderService } from '@shared/services';
import { CLERK_CREATE_APP_TOAST_CONFIG } from '../../shared/constants/clerk-create-app-toast.constants';
import { FormErrorService } from '@shared/components';
import { extractErrorMessage } from '@shared/utils';

@Injectable({
  providedIn: 'root',
})
export class ClerkCreateEstateFacadeService {
  private readonly _formService = inject(RegisterProbateFormService);
  private readonly _formErrorService = inject(FormErrorService);

  private readonly _toast = inject(ToastService);
  private readonly _probateService = inject(ProbateService);
  private readonly _subscriberStateService = inject(SubscribersStateService);
  private readonly _router = inject(Router);
  private readonly _clerkCreateApplicationHttpService = inject(
    ClerkCreateApplicationHttpService,
  );
  private readonly _dialogRef = inject(DialogRef);
  private readonly _probateNavigationService = inject(
    DashboardNavigationService,
  );
  private readonly _courtService = inject(CourtService);
  private readonly _loaderService = inject(LoaderService);

  private readonly _form = this._formService.registerForm;

  public submitApplication() {
    if (this._form.invalid) return;

    const rawPayload = this._form.getRawValue();
    const payload = {
      ...rawPayload,
      phoneNumber: getPhoneNumberToPlainNumber(rawPayload.phoneNumber),
      userId: rawPayload.userId === 0 ? null : rawPayload.userId,
    };

    this._loaderService.show();
    this._formService.setIsLoading(true);
    this._clerkCreateApplicationHttpService
      .createClerkProbate(Number(this._courtService.courtId()), payload)
      .pipe(
        tap((response) => {
          this._probateService.setTargetProbateId(Number(response.probate_id));
          this._subscriberStateService.setSelectedSubscriberId(response.id);
        }),
        tap(() => {
          this._probateNavigationService.goToDashboard();
        }),
        waitForNavigationEnd(this._router, this._toast),
        tap(() => {
          this._dialogRef.close();
        }),
        finalize(() => {
          this._formService.setIsLoading(false);
          this._loaderService.hide();
        }),

        catchError((error) => {
          const errorMessage = extractErrorMessage(error);

          this._formErrorService.displaySimpleErrorModal(
            errorMessage,
            'Error in Register',
          );
          this._toast.show(CLERK_CREATE_APP_TOAST_CONFIG['error']);
          return of(null);
        }),
      )
      .subscribe();
  }
}
